import { useEffect } from 'react';

import { announce, type AnnounceMode } from './liveAnnouncerStore';

export interface UseAnnounceOptions {
  /** The text to announce. Re-announced whenever this value changes. */
  message: string;
  mode?: AnnounceMode;
  /**
   * Delay before announcing, in milliseconds. A short delay lets the DOM swap
   * and focus changes that accompany a navigation settle first, so the screen
   * reader doesn't clobber/interrupt the announcement. Defaults to `500` — the
   * common sweet spot for page/route announcements.
   */
  delayMs?: number;
}

/**
 * Declaratively announce `message` to assistive technology whenever it changes,
 * by publishing to the shared {@link announce} store.
 *
 * Typically used to announce a page heading on navigation. Requires a single
 * `<LiveAnnouncer />` mounted at the app root to own the DOM live regions.
 *
 * @example
 * ```tsx
 * useAnnounce({ message: heading });
 * ```
 */
export const useAnnounce = ({
  message,
  mode = 'polite',
  delayMs = 500,
}: UseAnnounceOptions): void => {
  useEffect(() => {
    const timeout = setTimeout(() => announce(message, mode), delayMs);
    return () => clearTimeout(timeout);
  }, [message, mode, delayMs]);
};
